/**
 * LabelDesigner — a drag-and-drop layout builder for label templates.
 *
 * The output is a JSON layout stored in `template.extras.layout`. When
 * present, the print/preview pipeline (`LabelPreview.jsx`) uses it INSTEAD
 * of the built-in generic renderer, so a customised template renders the
 * same on-screen, in the browser print dialog, and over Niimbot BLE.
 *
 * Layout JSON schema (v1):
 *   {
 *     "version": 1,
 *     "elements": [
 *       {
 *         "id": "e1", "type": "text"|"field"|"qr"|"barcode"|"line",
 *         "x": 5, "y": 5,          // mm from top-left
 *         "w": 30, "h": 10,        // mm (for qr/barcode/line — text auto-sizes)
 *         "text": "Handcrafted",   // type=text only
 *         "field": "name",         // type=field: name|sku|dimension|price|carat|metal|ring_size|color|origin|shape
 *         "size": 8,               // font-size in pt (text/field only)
 *         "weight": 700,           // font-weight (text/field only)
 *         "family": "serif"|"sans"|"mono",
 *         "color": "#000000",
 *         "align": "left"|"center"|"right"
 *       }
 *     ]
 *   }
 *
 * Access: Super Admin only. Frontend blocks the entry route; backend keeps
 * the existing `POST/PATCH /api/labels/templates` permissions (admin/staff)
 * but a Super Admin toggle in the UI is what actually persists here so an
 * accidental staff click doesn't wipe a template.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import { Trash2, Copy, Save, Plus, Type, Tag as TagIcon, QrCode, BarChart3, Minus, MoveHorizontal, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { formatIDR } from "../lib/labelFormat";

const MM_TO_PX = 3.7795275591;
const PREVIEW_SCALE = 8; // 1 mm = 8×3.78 = ~30 CSS px — plenty for drag precision

const ELEMENT_PALETTE = [
  { type: "text",    icon: Type,      label: "Text",         seed: { text: "Handcrafted", w: 20, h: 6, size: 7, weight: 500, family: "sans", color: "#333", align: "left" } },
  { type: "field",   icon: TagIcon,   label: "Product field", seed: { field: "name", w: 30, h: 6, size: 8, weight: 700, family: "serif", color: "#000", align: "left" } },
  { type: "qr",      icon: QrCode,    label: "QR code",       seed: { w: 15, h: 15 } },
  { type: "barcode", icon: BarChart3, label: "Barcode",       seed: { w: 30, h: 8 } },
  { type: "line",    icon: Minus,     label: "Divider line",  seed: { w: 30, h: 0.3, color: "#D4AF37" } },
];

const FIELD_OPTIONS = [
  { key: "name", label: "Name" },
  { key: "sku", label: "SKU" },
  { key: "dimension", label: "Dimension" },
  { key: "price", label: "Price (IDR)" },
  { key: "carat", label: "Carat / Weight" },
  { key: "metal", label: "Metal" },
  { key: "ring_size", label: "Ring size" },
  { key: "color", label: "Color" },
  { key: "shape", label: "Shape" },
  { key: "origin", label: "Origin" },
];

// Demo product for the designer preview (real names/values pulled from a
// live product would drift the layout every time inventory changes).
const DEMO_PRODUCT = {
  name: "Natural Ruby",
  sku: "OJ-RUBY-00042",
  dimension: "8×6×4mm",
  selling_price: 2500000,
  carat: 1.85,
  metal: "silver_925",
  ring_size: "6",
  color: "Pigeon-blood red",
  shape: "Oval",
  origin: "Burma",
};

function newId() {
  return `e_${Math.random().toString(36).slice(2, 9)}`;
}

function fieldValue(field, product) {
  if (field === "price") return formatIDR(product.selling_price);
  if (field === "carat") return product.carat ? `${Number(product.carat).toFixed(2)} ct` : "";
  return String(product[field] ?? "");
}

export default function LabelDesigner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dimensions, setDimensions] = useState({ w: 75, h: 25 });
  const [elements, setElements] = useState([]);
  const [selectedEl, setSelectedEl] = useState(null);
  const [templateName, setTemplateName] = useState("New custom template");
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef(null);
  const dragState = useRef({});

  // -------- Only super admins can persist a layout. UI still visible for
  // preview so anyone can play with it, but the "Save" button is gated. --
  const canSave = !!user?.is_super_admin;

  useEffect(() => {
    api.get("/labels/templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);

  // -------- Load a template into the canvas --------
  const loadTemplate = (tid) => {
    const tpl = templates.find((t) => t.id === tid);
    if (!tpl) return;
    setSelectedId(tid);
    setTemplateName(`${tpl.name} (custom)`);
    setDimensions({ w: tpl.width_mm, h: tpl.height_mm });
    const layout = tpl.extras?.layout;
    if (layout?.elements?.length) {
      setElements(layout.elements.map((e) => ({ ...e, id: e.id || newId() })));
    } else {
      // Seed a starting layout from the template's built-in field flags —
      // gives the designer something to nudge around rather than a blank sheet.
      const seed = [];
      let y = 2;
      if (tpl.show_name) seed.push({ id: newId(), type: "field", field: "name", x: 2, y, w: tpl.width_mm - 4, h: 6, size: 9, weight: 700, family: "serif", color: "#000", align: "left" });
      y += 6;
      if (tpl.show_sku) seed.push({ id: newId(), type: "field", field: "sku", x: 2, y, w: 30, h: 5, size: 6, weight: 500, family: "mono", color: "#333", align: "left" });
      y += 5;
      if (tpl.show_price) seed.push({ id: newId(), type: "field", field: "price", x: 2, y, w: 30, h: 6, size: 8, weight: 700, family: "sans", color: "#D4AF37", align: "left" });
      if (tpl.show_qr) seed.push({ id: newId(), type: "qr", x: tpl.width_mm - 15, y: 2, w: 12, h: 12 });
      setElements(seed);
    }
    setSelectedEl(null);
  };

  // -------- Start with a blank canvas --------
  const startBlank = () => {
    setSelectedId(null);
    setTemplateName("New custom template");
    setDimensions({ w: 75, h: 25 });
    setElements([]);
    setSelectedEl(null);
  };

  // -------- Drop from palette --------
  const addElement = (paletteItem) => {
    const el = { id: newId(), type: paletteItem.type, x: 5, y: 5, ...paletteItem.seed };
    setElements((prev) => [...prev, el]);
    setSelectedEl(el.id);
  };

  const updateSelected = (patch) => {
    if (!selectedEl) return;
    setElements((prev) => prev.map((e) => (e.id === selectedEl ? { ...e, ...patch } : e)));
  };

  const deleteSelected = () => {
    if (!selectedEl) return;
    setElements((prev) => prev.filter((e) => e.id !== selectedEl));
    setSelectedEl(null);
  };

  // Delete key
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "Delete" || e.key === "Backspace") && selectedEl) {
        // Don't hijack the key while typing inside an input.
        if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
        e.preventDefault();
        deleteSelected();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEl]);

  // -------- Drag handlers (SVG-space → mm) --------
  const startDrag = (e, elId) => {
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const pxPerMm = rect.width / dimensions.w;
    const el = elements.find((x) => x.id === elId);
    dragState.current = { elId, startX: e.clientX, startY: e.clientY, origX: el.x, origY: el.y, pxPerMm };
    setSelectedEl(elId);
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", endDrag, { once: true });
  };
  const onDragMove = (e) => {
    const { elId, startX, startY, origX, origY, pxPerMm } = dragState.current;
    if (!elId) return;
    const dxMm = (e.clientX - startX) / pxPerMm;
    const dyMm = (e.clientY - startY) / pxPerMm;
    setElements((prev) => prev.map((el) => {
      if (el.id !== elId) return el;
      const nx = Math.max(0, Math.min(dimensions.w - (el.w || 5), origX + dxMm));
      const ny = Math.max(0, Math.min(dimensions.h - (el.h || 2), origY + dyMm));
      return { ...el, x: +nx.toFixed(2), y: +ny.toFixed(2) };
    }));
  };
  const endDrag = () => {
    dragState.current = {};
    window.removeEventListener("mousemove", onDragMove);
  };

  const save = async () => {
    if (!canSave) return toast.error("Only Super Admin can save templates");
    if (!templateName.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      // Mirror layout element usage to the top-level show_* flags so the
      // Print Label modal's QR/Barcode toggles default to ON when the
      // designer placed those elements. The generic renderer never runs
      // on a custom layout, so these flags only serve the modal defaults.
      const hasQR = elements.some((e) => e.type === "qr");
      const hasBarcode = elements.some((e) => e.type === "barcode");
      const payload = {
        name: templateName,
        kind: "custom",
        width_mm: dimensions.w,
        height_mm: dimensions.h,
        font_size_pt: 8,
        show_name: false, show_sku: false, show_dimension: false,
        show_ring_size: false, show_metal: false, show_price: false,
        show_qr: hasQR, show_barcode: hasBarcode,
        extras: { layout: { version: 1, elements } },
      };
      const created = await api.post("/labels/templates", payload);
      toast.success(`Template saved · ${created.data.name}`);
      setTemplates((prev) => [...prev, created.data]);
      setSelectedId(created.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const selEl = elements.find((e) => e.id === selectedEl) || null;

  const wPx = dimensions.w * MM_TO_PX * PREVIEW_SCALE;
  const hPx = dimensions.h * MM_TO_PX * PREVIEW_SCALE;

  return (
    <Layout>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-[0.65rem] uppercase tracking-wider text-[#8A8A8A] hover:text-[#D4AF37] flex items-center gap-1.5 mb-2"
            data-testid="designer-back"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
          <div className="overline">Label Template Designer</div>
          <h1 className="serif text-3xl mt-1">Drag & drop layout builder</h1>
          <p className="text-xs text-[#8A8A8A] mt-2 max-w-xl">
            Design a custom label layout by dragging elements onto the canvas. Elements auto-fill
            with real product data when printed (name → real gemstone name, price → real IDR).
            {!canSave && <span className="text-[#C99356]"> · Preview mode — only Super Admin can save.</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={selectedId || ""}
            onChange={(e) => (e.target.value ? loadTemplate(e.target.value) : startBlank())}
            className="luxe-input px-4 py-2.5 text-sm"
            data-testid="designer-template-select"
          >
            <option value="">— Start blank —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name} · {t.width_mm}×{t.height_mm}mm</option>
            ))}
          </select>
          <button
            onClick={save}
            disabled={!canSave || saving}
            className="btn-gold px-5 py-2.5 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
            data-testid="designer-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save as new"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_260px] gap-6">
        {/* -------- Palette -------- */}
        <div className="glass-card p-4">
          <div className="overline text-[0.55rem] mb-3">Elements</div>
          {ELEMENT_PALETTE.map((item) => (
            <button
              key={item.type}
              onClick={() => addElement(item)}
              className="w-full flex items-center gap-3 px-3 py-3 text-sm border border-white/10 hover:border-[#D4AF37]/60 bg-black/20 hover:bg-[#D4AF37]/[0.05] mb-2 text-left transition-colors"
              data-testid={`designer-add-${item.type}`}
            >
              <item.icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
              <span>{item.label}</span>
              <Plus className="w-3.5 h-3.5 ml-auto text-[#666]" />
            </button>
          ))}
          <div className="overline text-[0.55rem] mt-6 mb-3">Canvas size (mm)</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[0.65rem] mono text-[#8A8A8A]">
              Width
              <input
                type="number" min="10" max="200" step="1"
                value={dimensions.w}
                onChange={(e) => setDimensions((d) => ({ ...d, w: Math.max(10, Math.min(200, +e.target.value || 40)) }))}
                className="luxe-input w-full px-2 py-1.5 text-sm mono mt-1"
                data-testid="designer-canvas-w"
              />
            </label>
            <label className="text-[0.65rem] mono text-[#8A8A8A]">
              Height
              <input
                type="number" min="10" max="200" step="1"
                value={dimensions.h}
                onChange={(e) => setDimensions((d) => ({ ...d, h: Math.max(10, Math.min(200, +e.target.value || 25)) }))}
                className="luxe-input w-full px-2 py-1.5 text-sm mono mt-1"
                data-testid="designer-canvas-h"
              />
            </label>
          </div>
          <div className="mt-6">
            <label className="overline text-[0.55rem]">Template name</label>
            <input
              type="text" value={templateName} onChange={(e) => setTemplateName(e.target.value)}
              className="luxe-input w-full px-3 py-2 text-sm mt-1"
              data-testid="designer-template-name"
            />
          </div>
        </div>

        {/* -------- Canvas -------- */}
        <div className="glass-card p-6 flex flex-col items-center overflow-auto min-h-[400px]" onClick={() => setSelectedEl(null)}>
          <div className="text-[0.65rem] mono text-[#8A8A8A] mb-3">
            {dimensions.w} × {dimensions.h} mm · click element to select · drag to move
          </div>
          <svg
            ref={canvasRef}
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${wPx} ${hPx}`}
            width={wPx}
            height={hPx}
            style={{ background: "#FFFFFF", boxShadow: "0 0 0 1px #D4AF37", maxWidth: "100%", height: "auto" }}
            data-testid="designer-canvas"
          >
            <rect x="0" y="0" width={wPx} height={hPx} fill="#FFFFFF" />
            {elements.map((el) => (
              <DesignerElement
                key={el.id} el={el}
                selected={el.id === selectedEl}
                onMouseDown={(e) => startDrag(e, el.id)}
                scale={PREVIEW_SCALE}
              />
            ))}
          </svg>
        </div>

        {/* -------- Properties -------- */}
        <div className="glass-card p-4">
          <div className="overline text-[0.55rem] mb-3 flex items-center justify-between">
            <span>Selected element</span>
            {selEl && (
              <button onClick={deleteSelected} className="text-[#C99356] hover:text-red-400" data-testid="designer-delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {!selEl ? (
            <div className="text-xs text-[#666]">Select an element on the canvas to edit it.</div>
          ) : (
            <div className="space-y-4 text-xs">
              <div className="mono text-[#8A8A8A] uppercase tracking-wider text-[0.6rem]">
                {selEl.type} · #{selEl.id.slice(-4)}
              </div>
              {selEl.type === "text" && (
                <label className="block">
                  <span className="overline text-[0.55rem]">Text</span>
                  <input type="text" value={selEl.text || ""}
                         onChange={(e) => updateSelected({ text: e.target.value })}
                         className="luxe-input w-full px-2 py-1.5 text-sm mt-1" />
                </label>
              )}
              {selEl.type === "field" && (
                <label className="block">
                  <span className="overline text-[0.55rem]">Product field</span>
                  <select value={selEl.field} onChange={(e) => updateSelected({ field: e.target.value })}
                          className="luxe-input w-full px-2 py-1.5 text-sm mt-1">
                    {FIELD_OPTIONS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </select>
                </label>
              )}
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="overline text-[0.55rem]">X (mm)</span>
                  <input type="number" step="0.5" value={selEl.x}
                         onChange={(e) => updateSelected({ x: +e.target.value })}
                         className="luxe-input w-full px-2 py-1.5 text-sm mono mt-1" />
                </label>
                <label className="block">
                  <span className="overline text-[0.55rem]">Y (mm)</span>
                  <input type="number" step="0.5" value={selEl.y}
                         onChange={(e) => updateSelected({ y: +e.target.value })}
                         className="luxe-input w-full px-2 py-1.5 text-sm mono mt-1" />
                </label>
                <label className="block">
                  <span className="overline text-[0.55rem]">W (mm)</span>
                  <input type="number" step="0.5" value={selEl.w || 0}
                         onChange={(e) => updateSelected({ w: +e.target.value })}
                         className="luxe-input w-full px-2 py-1.5 text-sm mono mt-1" />
                </label>
                <label className="block">
                  <span className="overline text-[0.55rem]">H (mm)</span>
                  <input type="number" step="0.5" value={selEl.h || 0}
                         onChange={(e) => updateSelected({ h: +e.target.value })}
                         className="luxe-input w-full px-2 py-1.5 text-sm mono mt-1" />
                </label>
              </div>
              {(selEl.type === "text" || selEl.type === "field") && (
                <>
                  <label className="block">
                    <span className="overline text-[0.55rem]">Font size (pt)</span>
                    <input type="range" min="5" max="24" step="1" value={selEl.size || 8}
                           onChange={(e) => updateSelected({ size: +e.target.value })}
                           className="w-full accent-[#D4AF37] mt-1" />
                    <span className="mono text-[0.6rem] text-[#8A8A8A]">{selEl.size || 8}pt</span>
                  </label>
                  <label className="block">
                    <span className="overline text-[0.55rem]">Weight</span>
                    <select value={selEl.weight || 400} onChange={(e) => updateSelected({ weight: +e.target.value })}
                            className="luxe-input w-full px-2 py-1.5 text-sm mt-1">
                      <option value="400">Regular</option>
                      <option value="500">Medium</option>
                      <option value="700">Bold</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="overline text-[0.55rem]">Font family</span>
                    <select value={selEl.family || "sans"} onChange={(e) => updateSelected({ family: e.target.value })}
                            className="luxe-input w-full px-2 py-1.5 text-sm mt-1">
                      <option value="sans">Sans (Manrope)</option>
                      <option value="serif">Serif (Cormorant)</option>
                      <option value="mono">Mono</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="overline text-[0.55rem]">Alignment</span>
                    <select value={selEl.align || "left"} onChange={(e) => updateSelected({ align: e.target.value })}
                            className="luxe-input w-full px-2 py-1.5 text-sm mt-1">
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </label>
                </>
              )}
              <label className="block">
                <span className="overline text-[0.55rem]">Colour</span>
                <div className="flex gap-2 mt-2">
                  {["#000000", "#333333", "#D4AF37", "#7BA585", "#C99356"].map((c) => (
                    <button key={c} type="button" onClick={() => updateSelected({ color: c })}
                            className={`w-8 h-8 border ${selEl.color === c ? "border-white ring-2 ring-[#D4AF37]" : "border-white/20"}`}
                            style={{ background: c }} />
                  ))}
                </div>
              </label>
            </div>
          )}
        </div>
      </div>

      <Toaster position="top-center" theme="dark" />
    </Layout>
  );
}


/**
 * One element on the canvas — text / field / QR placeholder / barcode
 * placeholder / line. Interactive: click to select, drag to move.
 * Uses the demo product for a stable preview.
 */
function DesignerElement({ el, selected, onMouseDown, scale }) {
  const xPx = el.x * MM_TO_PX * scale;
  const yPx = el.y * MM_TO_PX * scale;
  const wPx = (el.w || 20) * MM_TO_PX * scale;
  const hPx = (el.h || 6) * MM_TO_PX * scale;
  const highlight = selected ? "#D4AF37" : "transparent";

  const family = el.family === "serif" ? "'Cormorant Garamond', serif"
    : el.family === "mono" ? "'Menlo', monospace"
    : "'Manrope', sans-serif";

  const commonProps = { onMouseDown, style: { cursor: "grab" } };

  const value = el.type === "field" ? fieldValue(el.field, DEMO_PRODUCT)
    : el.type === "text" ? (el.text || "")
    : "";

  return (
    <g {...commonProps} data-el-id={el.id}>
      {selected && (
        <rect x={xPx - 4} y={yPx - 4} width={wPx + 8} height={hPx + 8}
              fill="none" stroke={highlight} strokeWidth={1.5} strokeDasharray="4,3" />
      )}
      {(el.type === "text" || el.type === "field") && (
        <text
          x={el.align === "center" ? xPx + wPx / 2 : el.align === "right" ? xPx + wPx : xPx}
          y={yPx + (el.size || 8) * scale * 0.9}
          fontFamily={family}
          fontSize={(el.size || 8) * scale}
          fontWeight={el.weight || 400}
          textAnchor={el.align === "center" ? "middle" : el.align === "right" ? "end" : "start"}
          fill={el.color || "#000"}
        >
          {`${value || "…"}`}
        </text>
      )}
      {el.type === "qr" && (
        <rect x={xPx} y={yPx} width={wPx} height={hPx}
              fill="#EFEFEF" stroke="#999" strokeWidth={1} strokeDasharray="3,3" />
      )}
      {el.type === "qr" && (
        <text x={xPx + wPx / 2} y={yPx + hPx / 2 + 4} fontFamily="monospace"
              fontSize={11 * scale / 4} fill="#666" textAnchor="middle">QR</text>
      )}
      {el.type === "barcode" && (
        <>
          <rect x={xPx} y={yPx} width={wPx} height={hPx} fill="#EFEFEF" stroke="#999" strokeWidth={1} strokeDasharray="3,3" />
          <text x={xPx + wPx / 2} y={yPx + hPx / 2 + 4} fontFamily="monospace"
                fontSize={11 * scale / 4} fill="#666" textAnchor="middle">|||| ||| ||||</text>
        </>
      )}
      {el.type === "line" && (
        <line x1={xPx} y1={yPx} x2={xPx + wPx} y2={yPx}
              stroke={el.color || "#D4AF37"} strokeWidth={Math.max(1, hPx || 2)} />
      )}
    </g>
  );
}
